import React, { useState, useRef } from 'react';
import { Store, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AttivitaRequestForm = () => {
  // 1. Inizializziamo il timer al caricamento del componente
  const formStartTime = useRef(Math.floor(Date.now() / 1000));

  const [formData, setFormData] = useState({
    nome_attivita: '',
    partita_iva: '',
    referente: '',
    email: '',
    telefono: '',
    indirizzo: '',
    note: '',
    website_url_check: '' // Campo Honeypot
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.nome_attivita.trim()) return 'Il nome dell\'attività è obbligatorio';
    if (!formData.referente.trim()) return 'Il referente è obbligatorio';
    if (!formData.email.trim()) return 'L\'email è obbligatoria';
    if (!formData.telefono.trim()) return 'Il telefono è obbligatorio';
    if (!formData.indirizzo.trim()) return 'L\'indirizzo è obbligatorio';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Inserisci un\'email valida';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 2. Controllo Honeypot
    if (formData.website_url_check) {
      console.warn("Spam bot detected via honeypot.");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    // 3. Preparazione Payload con Time Trap CORRETTO
    const payload = {
        ...formData,
        form_start_time: formStartTime.current 
    };

    try {
      // Invio del payload modificato
      await axios.post(`${API_URL}/requests/attivita`, payload);
      setSubmitted(true);
      
      // Reset del form
      setFormData({
        nome_attivita: '',
        partita_iva: '',
        referente: '',
        email: '',
        telefono: '',
        indirizzo: '',
        note: '',
        website_url_check: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Si è verificato un errore. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
      setSubmitted(false);
      // 4. Reset del timer per una nuova compilazione
      formStartTime.current = Math.floor(Date.now() / 1000);
  };

  if (submitted) {
    return (
      <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-teal-50">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Richiesta Inviata!
            </h3>
            <p className="text-gray-700 max-w-md mx-auto mb-6">
              Grazie! La tua attività è stata inviata per l'inserimento nel circuito VeCo Shop. Ti contatteremo a breve.
            </p>
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              Invia un'altra richiesta
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-purple-200 hover:shadow-xl transition-shadow">
      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
            <Store className="w-6 h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl">SEI UN'ATTIVITÀ?</CardTitle>
          </div>
        </div>
        <CardDescription className="text-base font-semibold text-gray-800">
          ENTRA NEL CIRCUITO VECO SHOP
        </CardDescription>
        <CardDescription className="text-base mt-2">
          Compila il modulo di pre-adesione per registrare la tua attività e iniziare a far parte del circuito di vantaggi VeCo Shop
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome Attività */}
          <div>
            <Label htmlFor="nome_attivita">Nome Attività *</Label>
            <Input
              id="nome_attivita"
              value={formData.nome_attivita}
              onChange={(e) => handleChange('nome_attivita', e.target.value)}
              placeholder="Es: Pizzeria da Mario"
              className="border-gray-300 focus:border-purple-500"
              required
            />
          </div>

          {/* Partita IVA e Referente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="partita_iva">Partita IVA (consigliato)</Label>
              <Input
                id="partita_iva"
                value={formData.partita_iva}
                onChange={(e) => handleChange('partita_iva', e.target.value)}
                placeholder="12345678901"
                className="border-gray-300 focus:border-purple-500"
              />
            </div>
            <div>
              <Label htmlFor="referente">Referente *</Label>
              <Input
                id="referente"
                value={formData.referente}
                onChange={(e) => handleChange('referente', e.target.value)}
                placeholder="Mario Rossi"
                className="border-gray-300 focus:border-purple-500"
                required
              />
            </div>
          </div>

          {/* Email e Telefono */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@tuaattivita.it"
                className="border-gray-300 focus:border-purple-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="telefono">Telefono *</Label>
              <Input
                id="telefono"
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="+39 0571 123456"
                className="border-gray-300 focus:border-purple-500"
                required
              />
            </div>
          </div>

          {/* Indirizzo */}
          <div>
            <Label htmlFor="indirizzo">Indirizzo Attività *</Label>
            <Input
              id="indirizzo"
              value={formData.indirizzo}
              onChange={(e) => handleChange('indirizzo', e.target.value)}
              placeholder="Via Roma 100, 50053 Empoli (FI)"
              className="border-gray-300 focus:border-purple-500"
              required
            />
          </div>

          {/* Note */}
          <div>
            <Label htmlFor="note">Note (facoltativo)</Label>
            <Textarea
              id="note"
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              placeholder="Descrivi brevemente la tua attività e lo sconto che vorresti offrire..."
              rows={3}
              className="border-gray-300 focus:border-purple-500"
            />
          </div>

          {/* --- ANTISPAM FIELDS (Nascosti) --- */}
          <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <label htmlFor="website_url_check">Non compilare questo campo se sei umano:</label>
              <input 
                  type="text" 
                  id="website_url_check"
                  name="website_url_check" 
                  value={formData.website_url_check || ''} 
                  onChange={(e) => handleChange('website_url_check', e.target.value)} 
                  tabIndex="-1" 
                  autoComplete="off"
              />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg py-6"
            disabled={loading}
          >
            {loading ? 'Invio in corso...' : 'Richiedi Inserimento'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};